"use client";

import { useState } from "react";
import {
  motion,
  AnimatePresence,
  LayoutGroup,
  useReducedMotion,
} from "framer-motion";
import Link from "next/link";
import { ExternalLink } from "lucide-react";
import Image from "next/image";

interface Project {
  name: string;
  meta: string;
  image?: string;
  url: string;
}

function isVideo(src: string) {
  return /\.(mp4|webm|mov)$/i.test(src);
}

const transition = {
  layout: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
} as const;

export default function ProjectList({ projects }: { projects: Project[] }) {
  const [active, setActive] = useState<number | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const safeTransition = prefersReducedMotion
    ? { layout: { duration: 0 } }
    : transition;

  return (
    <LayoutGroup>
      <div className="flex flex-col">
        {projects.map((project, i) => {
          const isActive = active === i;

          return (
            <Link
              href={project.url}
              key={project.name}
              className="w-full"
              target="_blank"
            >
              <motion.div
                key={project.name}
                layout
                transition={safeTransition}
                className={`relative cursor-pointer border-t border-black/10 overflow-hidden ${
                  isActive
                    ? "bg-neutral-900 px-4 sm:px-6 py-6 rounded-xl"
                    : "py-5"
                }`}
                onHoverStart={() => setActive(i)}
                onHoverEnd={() => setActive(null)}
                onTap={() => setActive(isActive ? null : i)}
                style={{ borderColor: isActive ? "transparent" : undefined }}
              >
                <motion.div
                  layout
                  transition={safeTransition}
                  className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-2 md:gap-0 items-center"
                >
                  <motion.span
                    layout="position"
                    transition={safeTransition}
                    className={`font-mono text-xs uppercase ${
                      isActive ? "text-gray-200" : "text-gray-500"
                    }`}
                  >
                    {project.meta}
                  </motion.span>
                  <motion.div
                    layout="position"
                    transition={safeTransition}
                    className="flex items-center gap-6"
                  >
                    <AnimatePresence mode="popLayout">
                      {isActive && project.image && (
                        <motion.div
                          key="media"
                          className="w-60 aspect-video rounded-lg hidden md:block shrink-0 overflow-hidden"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ delay: 0.1, duration: 0.2 }}
                        >
                          {isVideo(project.image) ? (
                            <video
                              src={project.image}
                              autoPlay
                              loop
                              muted
                              playsInline
                              className="w-full h-full object-cover aspect-video"
                            />
                          ) : (
                            <Image
                              src={project.image}
                              alt={project.name}
                              className="w-full h-full object-cover aspect-video"
                              width={640}
                              height={360}
                              preload
                            />
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <h3
                      className={`font-heading text-2xl sm:text-3xl md:text-4xl tracking-tight ${
                        isActive ? "text-white" : "text-black"
                      }`}
                    >
                      {project.name}
                    </h3>
                    <ExternalLink
                      aria-hidden="true"
                      className={`ml-auto ${isActive ? "text-white" : "text-black"}`}
                    />
                  </motion.div>
                </motion.div>
              </motion.div>
            </Link>
          );
        })}
      </div>
    </LayoutGroup>
  );
}
